import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import { AppShell } from "@/components/AppShell";
import { navForRole } from "@/lib/permissions";

import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OrderIntake from "@/pages/OrderIntake";
import OrderResult from "@/pages/OrderResult";
import ActionQueue from "@/pages/ActionQueue";
import HumanReview from "@/pages/HumanReview";
import DefensePackets from "@/pages/DefensePackets";
import DefensePacketView from "@/pages/DefensePacketView";
import PolicyLibrary from "@/pages/PolicyLibrary";
import Analytics from "@/pages/Analytics";
import AuditTrail from "@/pages/AuditTrail";
import Administration from "@/pages/Administration";
import ValidationMode from "@/pages/ValidationMode";
import PilotConfiguration from "@/pages/PilotConfiguration";
import OperatingModel from "@/pages/OperatingModel";
import Integrations from "@/pages/Integrations";
import DomainQuestions from "@/pages/DomainQuestions";
import PilotReadiness from "@/pages/PilotReadiness";
import RuleGovernance from "@/pages/RuleGovernance";
import RulesHarness from "@/pages/RulesHarness";
import PilotMeasurement from "@/pages/PilotMeasurement";
import PilotDenialTaxonomy from "@/pages/PilotDenialTaxonomy";
import AssumptionsRegister from "@/pages/AssumptionsRegister";

function Guard({ navId, children }) {
  const { roleCode } = useApp();
  const allowed = navForRole(roleCode).some((n) => n.id === navId);
  return allowed ? children : <Navigate to="/" replace />;
}

function Shell() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/new" element={<Guard navId="orders"><OrderIntake /></Guard>} />
        <Route path="/orders/:id" element={<OrderResult />} />
        <Route path="/action-queue" element={<Guard navId="action_queue"><ActionQueue /></Guard>} />
        <Route path="/human-review" element={<Guard navId="human_review"><HumanReview /></Guard>} />
        <Route path="/defense-packets" element={<Guard navId="defense_packets"><DefensePackets /></Guard>} />
        <Route path="/defense-packets/:id" element={<DefensePacketView />} />
        <Route path="/policy-library" element={<Guard navId="policy_library"><PolicyLibrary /></Guard>} />
        <Route path="/analytics" element={<Guard navId="analytics"><Analytics /></Guard>} />
        <Route path="/audit" element={<Guard navId="audit_trail"><AuditTrail /></Guard>} />
        <Route path="/administration" element={<Guard navId="administration"><Administration /></Guard>} />
        <Route path="/validation" element={<ValidationMode />} />
        <Route path="/pilot-configuration" element={<Guard navId="pilot_config"><PilotConfiguration /></Guard>} />
        <Route path="/operating-model" element={<Guard navId="operating_model"><OperatingModel /></Guard>} />
        <Route path="/integrations" element={<Guard navId="integrations"><Integrations /></Guard>} />
        <Route path="/domain-questions" element={<Guard navId="domain_questions"><DomainQuestions /></Guard>} />
        <Route path="/pilot-readiness" element={<Guard navId="pilot_readiness"><PilotReadiness /></Guard>} />
        <Route path="/rule-governance" element={<Guard navId="rule_governance"><RuleGovernance /></Guard>} />
        <Route path="/rules-harness" element={<Guard navId="rules_harness"><RulesHarness /></Guard>} />
        <Route path="/pilot-measurement" element={<Guard navId="pilot_measurement"><PilotMeasurement /></Guard>} />
        <Route path="/denial-taxonomy" element={<Guard navId="denial_taxonomy"><PilotDenialTaxonomy /></Guard>} />
        <Route path="/assumptions" element={<Guard navId="assumptions"><AssumptionsRegister /></Guard>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Shell />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
